import React, { useEffect, useMemo } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ListNode, ListItemNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { twMerge } from 'tailwind-merge';

interface LexicalViewerProps {
  content: string; // JSON string of Lexical state
  className?: string;
}

// Plugin to set the editor state from JSON
function StatePlugin({ content }: { content: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (content) {
      try {
        const parsedState = JSON.parse(content);
        const editorState = editor.parseEditorState(parsedState);
        editor.setEditorState(editorState);
      } catch (e) {
        console.error('Failed to parse content:', e);
      }
    }
  }, [editor, content]);

  return null;
}

function LexicalViewer({ content, className = '' }: LexicalViewerProps) {
  const initialConfig = useMemo(
    () => ({
      namespace: 'DillViewer',
      nodes: [ListNode, ListItemNode],
      theme: {
        paragraph: 'mb-1',
        text: {
          bold: 'font-bold',
          italic: 'italic',
          underline: 'underline',
          strikethrough: 'line-through',
        },
        list: {
          ul: 'list-disc ml-4',
          ol: 'list-decimal ml-4',
          listitem: 'mb-1',
        },
        link: 'text-blue-600 underline',
      },
      onError: (error: Error) => {
        console.error(error);
      },
      editable: false, // Read-only mode
    }),
    []
  );

  // Handle empty or invalid content
  if (!content) {
    return <div className={twMerge('text-gray-400 italic', className)}>No content</div>;
  }

  return (
    <div className={twMerge('', className)}>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="relative outline-none overflow-y-auto"
              style={{ minHeight: 'auto' }}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ListPlugin />
        <StatePlugin content={content} />
      </LexicalComposer>
    </div>
  );
}

export default LexicalViewer;
